import { ECriteriaRatingType, ICriteriaRating, IPartnerRating } from '../../interfaces';
import { bucketBy } from '../common/ratingUtils';

export type CriterionValue = number | boolean;

export interface IPartnerRealEstate {
  realEstateGuid: string;
  realEstateName: string;
  row: IPartnerRating;
}

export interface IPartnerCriterion {
  criteriaRatingId: number;
  criteriaRatingName: string;
  type: ECriteriaRatingType;
}

export interface IPartnerGroup {
  key: string;
  partnerGuid: string;
  serviceTypeId: number;
  partnerName: string;
  serviceTypeName: string;
  /** Только если у пользователя по этому партнёру и виду услуг несколько оценивающих должностей. */
  positionName: string | null;
  /** Самая поздняя дата окончания договора по ЖК группы; null — у какого-то ЖК договор бессрочный. */
  contractEnd: string | null;
  realEstates: IPartnerRealEstate[];
  criteria: IPartnerCriterion[];
}

export interface ICriterionEntry {
  base: CriterionValue | null;
  overrides: Record<string, CriterionValue>;
}

export type PartnerRatingState = Record<string, ICriterionEntry>;

const EMPTY_ENTRY: ICriterionEntry = { base: null, overrides: {} };

const byName = (a: string, b: string) => a.localeCompare(b, 'ru');

function contractEndOf(rows: IPartnerRating[]): string | null {
  const ends = rows.map(({ contractEnd }) => contractEnd).filter((end): end is string => typeof end === 'string');
  if (ends.length < rows.length) return null;
  // Даты ISO (ГГГГ-ММ-ДД) — строковое сравнение совпадает с хронологическим
  return ends.reduce((latest, end) => (end.localeCompare(latest) > 0 ? end : latest), '');
}

function toGroup(key: string, rows: IPartnerRating[]): IPartnerGroup {
  const [first] = rows;
  return {
    key,
    partnerGuid: first.partnerGuid ?? '',
    serviceTypeId: first.serviceTypeId ?? 0,
    partnerName: first.partnerName ?? '',
    serviceTypeName: first.serviceTypeName ?? '',
    positionName: first.positionName ?? null,
    contractEnd: contractEndOf(rows),
    realEstates: rows
      .map((row) => ({ realEstateGuid: row.realEstateGuid ?? '', realEstateName: row.realEstateName ?? '', row }))
      .sort((a, b) => byName(a.realEstateName, b.realEstateName)),
    criteria: (first.criteriaRatings ?? []).map(({ criteriaRatingId, criteriaRatingName, type }) => ({
      criteriaRatingId,
      criteriaRatingName,
      type,
    })),
  };
}

const hasSiblingPosition = (group: IPartnerGroup, groups: IPartnerGroup[]) =>
  groups.some(
    (other) =>
      other.key !== group.key && other.partnerGuid === group.partnerGuid && other.serviceTypeId === group.serviceTypeId,
  );

/** Строки «партнёр × ЖК» → группы «партнёр + вид услуг (+ должность)». */
export function groupPartnerRatings(rows: IPartnerRating[]): IPartnerGroup[] {
  const buckets = bucketBy(rows, (row) => `${row.partnerGuid}|${row.serviceTypeId}|${row.positionId}`);
  const groups = Array.from(buckets.entries()).map(([key, groupRows]) => toGroup(key, groupRows));
  return groups
    .map((group) => ({ ...group, positionName: hasSiblingPosition(group, groups) ? group.positionName : null }))
    .sort((a, b) => byName(a.partnerName, b.partnerName) || byName(a.serviceTypeName, b.serviceTypeName));
}

export const criterionKey = (group: IPartnerGroup, { criteriaRatingId }: { criteriaRatingId: number }) =>
  `${group.key}#${criteriaRatingId}`;

export const entryOf = (state: PartnerRatingState, key: string): ICriterionEntry => state[key] ?? EMPTY_ENTRY;

/** Общая оценка; уточнения по ЖК сбрасываются. */
export const setBase = (state: PartnerRatingState, key: string, value: CriterionValue): PartnerRatingState => ({
  ...state,
  [key]: { base: value, overrides: {} },
});

/** Уточнение по ЖК; равное общей снимается; без общей оценки — без изменений. */
export function setOverride(
  state: PartnerRatingState,
  key: string,
  realEstateGuid: string,
  value: CriterionValue,
): PartnerRatingState {
  const entry = entryOf(state, key);
  if (entry.base === null) return state;
  const overrides = { ...entry.overrides };
  delete overrides[realEstateGuid];
  if (value !== entry.base) overrides[realEstateGuid] = value;
  return { ...state, [key]: { base: entry.base, overrides } };
}

export const resetOverrides = (state: PartnerRatingState, key: string): PartnerRatingState => ({
  ...state,
  [key]: { base: entryOf(state, key).base, overrides: {} },
});

export const restoreEntry = (state: PartnerRatingState, key: string, entry: ICriterionEntry): PartnerRatingState => ({
  ...state,
  [key]: entry,
});

export const overrideCount = (entry: ICriterionEntry) => Object.keys(entry.overrides).length;

export const valueFor = (entry: ICriterionEntry, realEstateGuid: string): CriterionValue | null =>
  entry.overrides[realEstateGuid] ?? entry.base;

/** Итог: звёзды — среднее по ЖК до десятых, Да/Нет — число ЖК с «Нет». */
export function effectiveValue(entry: ICriterionEntry, group: IPartnerGroup, criterion: IPartnerCriterion) {
  if (entry.base === null) return null;
  const values = group.realEstates.map(({ realEstateGuid }) => valueFor(entry, realEstateGuid));
  if (criterion.type === ECriteriaRatingType.Boolean) return values.filter((value) => value === false).length;
  const sum = values.reduce<number>((acc, value) => acc + Number(value), 0);
  return Math.round((sum / values.length) * 10) / 10;
}

export const filledCount = (group: IPartnerGroup, state: PartnerRatingState) =>
  group.criteria.filter((criterion) => entryOf(state, criterionKey(group, criterion)).base !== null).length;

export const remainingCount = (groups: IPartnerGroup[], state: PartnerRatingState) =>
  groups.reduce((acc, group) => acc + group.criteria.length - filledCount(group, state), 0);

export function firstUnfilled(groups: IPartnerGroup[], state: PartnerRatingState) {
  const groupIndex = groups.findIndex((group) => filledCount(group, state) < group.criteria.length);
  if (groupIndex < 0) return null;
  const group = groups[groupIndex];
  const criterion = group.criteria.find((item) => entryOf(state, criterionKey(group, item)).base === null);
  return criterion ? { groupIndex, key: criterionKey(group, criterion) } : null;
}

function toRowValue(criterion: ICriteriaRating, value: CriterionValue | null): number | null {
  if (value === null) return null;
  if (criterion.type === ECriteriaRatingType.Boolean)
    return value === true ? criterion.ratingTrue : criterion.ratingFalse;
  return Number(value);
}

function rowRequest(group: IPartnerGroup, { realEstateGuid, row }: IPartnerRealEstate, state: PartnerRatingState) {
  const criteriaRatings = (row.criteriaRatings ?? []).map((criterion) => ({
    ...criterion,
    value: toRowValue(criterion, valueFor(entryOf(state, criterionKey(group, criterion)), realEstateGuid)),
  }));
  return { ...row, criteriaRatings };
}

/** По строке на ЖК; строка уходит целиком — бэку нужны счётчики качества и dateAt. */
export const buildPartnerRequests = (groups: IPartnerGroup[], state: PartnerRatingState): IPartnerRating[] =>
  groups.flatMap((group) => group.realEstates.map((realEstate) => rowRequest(group, realEstate, state)));
